import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import SearchResultPage from './SearchResultPage'
import { searchWithLinkup } from './searchUtils'
// Dynamic import to avoid initialization issues
// import { AgentHistoryIntegration } from '../../utils/agentHistoryIntegration'
import { useCreditWarning } from '../../hooks/useCreditWarning'
import CreditWarningModal from '../../components/CreditWarningModal'
import { useSubscription } from '../../hooks/useSubscription'
import { Search } from 'lucide-react'
import { AGENT_TYPES } from '../index.js'

// Search Agent States
const SEARCH_STATES = {
  SEARCHING: 'searching',
  COMPLETED: 'completed'
}

const SearchAgent = ({ query, onBack, llmService, options = {} }) => {
  const [currentState, setCurrentState] = useState(SEARCH_STATES.SEARCHING)
  const [searchResult, setSearchResult] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [searchType] = useState('web')
  const [hasInitialized, setHasInitialized] = useState(false)
  const [isStreamingAnswer, setIsStreamingAnswer] = useState(false)
  const [streamedAnswer, setStreamedAnswer] = useState('')
  const { warningModal, checkAndWarnCredits, closeWarning } = useCreditWarning()
  const { consumeCredits } = useSubscription()
  
  // Use refs to track state and prevent unnecessary re-search
  const isSearching = useRef(false)
  const currentQuery = useRef(query)
  const hasEverSearched = useRef(false)
  const hasConsumedCredits = useRef(false)

  // Session storage keys for persistence
  const getSessionKey = (suffix) => `search_agent_${query}_${searchType}_${suffix}`
  const getCreditKey = () => `search_agent_credits_${query}_${searchType}`

  // Load persisted state on component mount
  useEffect(() => {
    const loadPersistedState = () => {
      try {
        const persistedState = sessionStorage.getItem(getSessionKey('state'))
        const persistedResult = sessionStorage.getItem(getSessionKey('result'))
        
        if (persistedState && persistedResult) {
          const state = JSON.parse(persistedState)
          const resultData = JSON.parse(persistedResult)
          
          console.log('📋 Loading persisted search state:', state.currentState)
          setCurrentState(state.currentState)
          setSearchResult(resultData)
          setHasInitialized(true)
          hasEverSearched.current = true
          
          // Don't search if we have persisted state
          return true
        }
      } catch (error) {
        console.warn('⚠️ Failed to load persisted state:', error)
        // Clear corrupted data
        try {
          sessionStorage.removeItem(getSessionKey('state'))
          sessionStorage.removeItem(getSessionKey('result'))
        } catch (clearError) {
          console.warn('⚠️ Failed to clear corrupted session data:', clearError)
        }
      }
      return false
    }
    
    // Only load persisted state if query matches current query
    if (currentQuery.current === query) {
      const hasPersistedState = loadPersistedState()
      if (hasPersistedState) {
        return // Skip search
      }
    } else {
      // Query changed, clear old session data
      try {
        const oldQuery = currentQuery.current
        sessionStorage.removeItem(`search_agent_${oldQuery}_web_state`)
        sessionStorage.removeItem(`search_agent_${oldQuery}_web_result`)
        sessionStorage.removeItem(`search_agent_${oldQuery}_academic_state`)
        sessionStorage.removeItem(`search_agent_${oldQuery}_academic_result`)
        sessionStorage.removeItem(`search_agent_credits_${oldQuery}_web`)
        sessionStorage.removeItem(`search_agent_credits_${oldQuery}_academic`)
      } catch (error) {
        console.warn('⚠️ Failed to clear old session data:', error)
      }
      currentQuery.current = query
      // Reset flags for new query
      hasEverSearched.current = false
      isSearching.current = false
      hasConsumedCredits.current = false
      setHasInitialized(false)
    }
    
    // Start search only if needed and prevent duplicate runs
    if (query && !hasInitialized && !isSearching.current && !hasEverSearched.current) {
      console.log('🚀 Starting initial search for new query')
      handleStartSearch()
    }
  }, [query]) // Remove searchType from dependency to prevent unnecessary re-runs

  // Persist state changes to session storage
  useEffect(() => {
    if (hasInitialized && searchResult) {
      try {
        sessionStorage.setItem(getSessionKey('state'), JSON.stringify({
          currentState,
          hasInitialized: true
        }))
        sessionStorage.setItem(getSessionKey('result'), JSON.stringify(searchResult))
      } catch (error) {
        console.warn('⚠️ Failed to persist state:', error)
      }
    }
  }, [currentState, searchResult, hasInitialized, query])

  // Helper function to save search result to history
   const saveToHistory = async (answer, currentSearchType = 'web', linkupData = null) => {
     try {
       const { AgentHistoryIntegration } = await import('../../utils/agentHistoryIntegration')
       
       // Use linkupData if provided, otherwise fall back to searchResult
       const dataSource = linkupData || searchResult || {}
       
       const saveData = {
         query: query,
         answer: answer || 'Tidak ada jawaban yang dihasilkan.',
         sources: dataSource.sources || [],
         images: dataSource.images || [],
         searchType: currentSearchType
       }
       
       console.log('🔍 Saving to history with data:', {
         query: saveData.query,
         answerLength: saveData.answer?.length || 0,
         sourcesCount: saveData.sources?.length || 0,
         imagesCount: saveData.images?.length || 0,
         searchType: saveData.searchType
       })
       
       await AgentHistoryIntegration.saveSearchResult(saveData)
       console.log('✅ Search result saved to history successfully')
     } catch (historyError) {
       console.error('❌ Error saving to history:', historyError)
     }
   }

  const handleStartSearch = async (customSearchType = searchType) => {
    if (isSearching.current || hasEverSearched.current) {
      console.log('⚠️ Search already in progress or completed, skipping...')
      return
    }
    
    // Check credits before starting search
    const canProceed = await checkAndWarnCredits(AGENT_TYPES.SEARCH)
    if (!canProceed) {
      return // Stop execution if insufficient credits
    }

    isSearching.current = true
    setCurrentState(SEARCH_STATES.SEARCHING)
    setIsLoading(true)
    setError(null)
    setSearchResult(null)
    setStreamedAnswer('')
    setIsStreamingAnswer(false)
    
    try {
      console.log(`🔄 Starting search for: ${query} with type: ${customSearchType}`)
      
      // First, get sources and images from Linkup
      const linkupResult = await searchWithLinkup(query, customSearchType, null) // No LLM enhancement initially
      
      // Show sources and images immediately
      const initialResult = {
        answer: '', // Empty initially
        sources: linkupResult.sources || [],
        images: linkupResult.images || [],
        searchType: customSearchType,
        region: linkupResult.region,
        rawSourcesCount: linkupResult.rawSourcesCount || 0,
        validSourcesCount: linkupResult.validSourcesCount || 0,
        hasFallbackSources: linkupResult.hasFallbackSources || false,
        enhancedWithLLM: false,
        isStreamingAnswer: false
      }
      
      setSearchResult(initialResult)
      setHasInitialized(true)
      console.log('✅ Sources and images loaded, starting answer streaming...')
      
      // Now stream the enhanced answer
       if (llmService && linkupResult.sources && linkupResult.sources.length > 0) {
         setIsStreamingAnswer(true)
         
         // Update search result to show streaming status
         setSearchResult(prev => ({
           ...prev,
           isStreamingAnswer: true
         }))
        
        // Prepare sources context for LLM
        const sourcesContext = linkupResult.sources.map((source, index) => 
          `[${index + 1}] ${source.title}\n${source.snippet}\nSumber: ${source.domain}`
        ).join('\n\n')
        
        // Create enhanced prompt for LLM
        const enhancementPrompt = `Berdasarkan pertanyaan pengguna dan sumber-sumber informasi berikut, berikan jawaban yang sangat komprehensif, detail, dan berkualitas tinggi dengan minimal 1000-1500 kata.

PERTANYAAN: ${query}

SUMBER INFORMASI:
${sourcesContext}

INSTRUKSI DETAIL:
1. STRUKTUR JAWABAN:
   - Mulai dengan pengantar yang menjelaskan konteks dan pentingnya topik
   - Bagi jawaban menjadi beberapa bagian utama dengan heading yang jelas
   - Setiap bagian harus memiliki minimal 2-3 paragraf yang detail
   - Akhiri dengan kesimpulan yang komprehensif dan implikasi praktis

2. KONTEN YANG HARUS DISERTAKAN:
   - Definisi dan konsep dasar yang relevan
   - Analisis mendalam dari berbagai aspek topik
   - Contoh konkret dan studi kasus jika relevan
   - Perbandingan dengan topik terkait atau alternatif
   - Perkembangan terkini dan tren masa depan
   - Dampak atau implikasi praktis
   - Perspektif dari berbagai sudut pandang

3. KUALITAS PENULISAN:
   - Gunakan bahasa yang jelas, formal, dan mudah dipahami
   - Setiap paragraf minimal 4-5 kalimat yang substantif
   - Gunakan transisi yang smooth antar paragraf
   - Berikan penjelasan yang detail dan tidak superficial
   - Hindari pengulangan informasi yang tidak perlu

4. PENGGUNAAN SUMBER:
   - Gunakan sitasi [1], [2], dst. untuk merujuk ke sumber yang relevan
   - Distribusikan sitasi secara merata di seluruh jawaban
   - Jika ada informasi yang bertentangan antar sumber, analisis dan jelaskan perbedaannya
   - Integrasikan informasi dari berbagai sumber untuk memberikan perspektif yang holistik

5. TARGET PANJANG:
   - Minimal 1000 kata, target ideal 1200-1500 kata
   - Pastikan setiap kata memberikan nilai tambah dan informasi yang berguna
   - Jangan menambahkan filler text, fokus pada konten yang berkualitas

6. FORMAT MARKDOWN:
   - Gunakan heading (##) untuk bagian utama
   - Gunakan subheading (###) untuk sub-bagian jika diperlukan
   - Gunakan bullet points atau numbered lists untuk informasi yang terstruktur
   - Gunakan **bold** untuk penekanan pada poin-poin penting
   - Gunakan *italic* untuk istilah teknis atau asing

JAWABAN KOMPREHENSIF:`
        
        // Declare fullAnswer outside try-catch so it's accessible in finally block
        let fullAnswer = ''
        
        try {
          // Set streaming status to true before starting
          setIsStreamingAnswer(true)
          setSearchResult(prev => ({
            ...prev,
            isStreamingAnswer: true
          }))
          
          // Stream the answer
          await llmService.sendStreamingMessage(
            [{ role: 'user', content: enhancementPrompt }],
            (chunk) => {
              // Extract content from the chunk object
              const content = chunk?.choices?.[0]?.delta?.content || ''
              if (content) {
                fullAnswer += content
                setStreamedAnswer(fullAnswer)
                
                // Update the search result with the streaming answer
                setSearchResult(prev => ({
                  ...prev,
                  answer: fullAnswer,
                  enhancedWithLLM: true,
                  isStreamingAnswer: true
                }))
              }
            },
            () => {
              console.log('✅ Answer streaming completed')
              // Note: History will be saved in finally block to avoid duplication
            },
            (error) => {
              console.error('❌ Streaming error:', error)
            },
            {
              mode: 'genius',
              searchType: customSearchType,
              additionalParams: {
                maxTokens: customSearchType === 'academic' ? 6000 : 5000
              }
            }
          )
        } catch (streamError) {
          console.warn('⚠️ Failed to stream answer, using fallback:', streamError)
          // Use fallback answer if streaming fails
          const fallbackAnswer = linkupResult.answer || 'Maaf, tidak dapat menemukan jawaban untuk pertanyaan Anda.'
          setStreamedAnswer(fallbackAnswer)
          setSearchResult(prev => ({
             ...prev,
             answer: fallbackAnswer,
             enhancedWithLLM: false,
             isStreamingAnswer: false
           }))
        } finally {
           setIsStreamingAnswer(false)
           // Update search result to remove streaming status
           setSearchResult(prev => ({
             ...prev,
             isStreamingAnswer: false
           }))
           // Save to history once after streaming is complete
           if (fullAnswer) {
             await saveToHistory(fullAnswer, customSearchType, linkupResult)
           }
         }
      } else {
        // No LLM service or sources, use original answer
        const fallbackAnswer = linkupResult.answer || 'Maaf, tidak dapat menemukan jawaban untuk pertanyaan Anda.'
        setStreamedAnswer(fallbackAnswer)
        setSearchResult(prev => ({
           ...prev,
           answer: fallbackAnswer,
           enhancedWithLLM: false,
           isStreamingAnswer: false
         }))
        // Save fallback answer to history (only if no fullAnswer was saved)
        if (!fullAnswer) {
          await saveToHistory(fallbackAnswer, customSearchType, linkupResult)
        }
      }
      
      hasEverSearched.current = true
      setCurrentState(SEARCH_STATES.COMPLETED)
      
      // Consume credits after successful search (only once per query)
      const creditKey = getCreditKey()
      const hasConsumedInSession = sessionStorage.getItem(creditKey)
      
      if (!hasConsumedCredits.current && !hasConsumedInSession) {
        try {
          await consumeCredits(AGENT_TYPES.SEARCH, {
            query: query,
            agentType: AGENT_TYPES.SEARCH,
            agentName: 'Search Agent',
            searchType: customSearchType
          })
          hasConsumedCredits.current = true
          sessionStorage.setItem(creditKey, 'true')
          console.log('✅ Credits consumed for Search Agent')
        } catch (creditError) {
          console.error('❌ Failed to consume credits for Search Agent:', creditError)
        }
      } else {
        console.log('⚠️ Credits already consumed for this search, skipping...')
      }
      
      // Note: Data will be saved to history after streaming is complete
    } catch (err) {
      console.error('❌ Error in handleStartSearch:', err)
      setError('Gagal melakukan pencarian. Silakan coba lagi.')
      setHasInitialized(false)
      hasEverSearched.current = false // Allow retry on error
    } finally {
      setIsLoading(false)
      isSearching.current = false
    }
  }

  // Search type is determined automatically by the system

  const handleRetry = () => {
    // Clear session data for retry
    try {
      sessionStorage.removeItem(getSessionKey('state'))
      sessionStorage.removeItem(getSessionKey('result'))
      sessionStorage.removeItem(getCreditKey())
    } catch (error) {
      console.warn('⚠️ Failed to clear session data for retry:', error)
    }
    
    setHasInitialized(false)
    hasEverSearched.current = false
    hasConsumedCredits.current = false
    setError(null)
    setSearchResult(null)
    setCurrentState(SEARCH_STATES.SEARCHING)
    
    // Start search again
    setTimeout(() => {
      handleStartSearch()
    }, 100)
  }

  // Show search type selector and loading state
  if (currentState === SEARCH_STATES.SEARCHING || isLoading) {
    return (
      <div className="min-h-screen bg-bg-primary p-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="bg-bg-tertiary rounded-xl shadow-lg p-6 mb-6 border border-border-primary">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-brand-primary/20 rounded-lg">
                  <Search className="w-6 h-6 text-brand-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-text-primary">Search Agent</h1>
                  <p className="text-text-secondary">Mencari informasi berkualitas dari sumber terpercaya</p>
                </div>
              </div>
              <button
                onClick={onBack}
                className="px-4 py-2 text-text-secondary hover:text-text-primary hover:bg-bg-quaternary rounded-lg transition-colors"
              >
                ← Kembali
              </button>
            </div>
            
            <div className="bg-brand-primary/10 rounded-lg p-4 border border-brand-primary/20">
              <p className="text-brand-primary font-medium mb-2">Pertanyaan:</p>
              <p className="text-text-primary">{query}</p>
            </div>
          </div>

          {/* Loading State */}
          <div className="bg-bg-tertiary rounded-xl shadow-lg p-8 border border-border-primary">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                Mencari informasi terbaik...
              </h3>
              <p className="text-text-secondary">
                Sedang menganalisis sumber-sumber terpercaya untuk memberikan jawaban terbaik
              </p>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 mt-6">
              <div className="flex items-center space-x-3">
                <div className="text-red-400">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-red-300 font-semibold">Terjadi Kesalahan</h3>
                  <p className="text-red-200">{error}</p>
                  <button
                    onClick={handleRetry}
                    className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Coba Lagi
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Credit Warning Modal */}
        {warningModal.isOpen && (
          <CreditWarningModal
            isOpen={warningModal.isOpen}
            onClose={closeWarning}
            message={warningModal.message}
            requiredCredits={warningModal.requiredCredits}
            currentCredits={warningModal.currentCredits}
          />
        )}
      </div>
    )
  }

  // Show results
  if (currentState === SEARCH_STATES.COMPLETED && searchResult) {
    return (
      <SearchResultPage
        query={query}
        searchResult={searchResult}
        searchType={searchType}
        onBack={onBack}
        onRetry={handleRetry}
      />
    )
  }

  return null
}

SearchAgent.propTypes = {
  query: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  llmService: PropTypes.object.isRequired,
  options: PropTypes.object
}

export default SearchAgent